import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { User, Camera, Trash, FloppyDisk, Spinner, EnvelopeSimple, Phone, Buildings, MapPin, Globe, IdentificationCard, Key } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
    const { user: authUser, checkAuth } = useAuth();
    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
    const [savingPassword, setSavingPassword] = useState(false);
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        position: '',
        address: '',
        city: '',
        zip_code: '',
        website: '',
        bio: '',
        avatar: null,
        role: '',
        created_at: ''
    });

    const fetchProfile = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/profile`, { withCredentials: true });
            setProfile({
                name: data.name || '',
                email: data.email || '',
                phone: data.phone || '',
                company: data.company || '',
                position: data.position || '',
                address: data.address || '',
                city: data.city || '',
                zip_code: data.zip_code || '',
                website: data.website || '',
                bio: data.bio || '',
                avatar: data.avatar || null,
                role: data.role || 'broker',
                created_at: data.created_at || ''
            });
        } catch (error) {
            toast.error('Fehler beim Laden des Profils');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/api/profile`, {
                name: profile.name,
                phone: profile.phone,
                company: profile.company,
                position: profile.position,
                address: profile.address,
                city: profile.city,
                zip_code: profile.zip_code,
                website: profile.website,
                bio: profile.bio
            }, { withCredentials: true });
            toast.success('Profil gespeichert');
            checkAuth();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
        setSaving(false);
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Maximale Dateigröße: 5 MB');
            return;
        }

        setUploadingAvatar(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await axios.post(`${API}/api/profile/avatar`, formData, { withCredentials: true });
            setProfile(prev => ({ ...prev, avatar: data.avatar }));
            toast.success('Profilbild aktualisiert');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
        }
        setUploadingAvatar(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDeleteAvatar = async () => {
        try {
            await axios.delete(`${API}/api/profile/avatar`, { withCredentials: true });
            setProfile(prev => ({ ...prev, avatar: null }));
            toast.success('Profilbild entfernt');
        } catch (error) {
            toast.error('Fehler beim Entfernen');
        }
    };

    const handleChangePassword = async () => {
        if (passwords.new.length < 6) {
            toast.error('Neues Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        if (passwords.new !== passwords.confirm) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }
        setSavingPassword(true);
        try {
            await axios.put(`${API}/api/profile/password`, {
                current_password: passwords.current,
                new_password: passwords.new
            }, { withCredentials: true });
            toast.success('Passwort geändert');
            setPasswords({ current: '', new: '', confirm: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
        }
        setSavingPassword(false);
    };

    const getAvatarUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return `${API}/api/storage/${path}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit', month: 'long', year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Spinner className="animate-spin mr-2" size={24} />
                <span className="text-muted-foreground">Profil laden...</span>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6" data-testid="profile-page">
            <div>
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    Mein Profil
                </h1>
                <p className="text-muted-foreground mt-1">
                    Persönliche Daten und Kontaktinformationen verwalten
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Avatar & Info Sidebar */}
                <Card className="border lg:col-span-1" data-testid="avatar-card">
                    <CardContent className="pt-6">
                        <div className="flex flex-col items-center text-center">
                            {/* Avatar */}
                            <div className="relative group mb-4">
                                <div
                                    className="w-28 h-28 rounded-full overflow-hidden bg-muted flex items-center justify-center border-2 border-border"
                                    data-testid="avatar-display"
                                >
                                    {profile.avatar ? (
                                        <img
                                            src={getAvatarUrl(profile.avatar)}
                                            alt="Profilbild"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <User size={48} className="text-muted-foreground" />
                                    )}
                                </div>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute inset-0 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                    disabled={uploadingAvatar}
                                    data-testid="avatar-upload-trigger"
                                >
                                    {uploadingAvatar ? (
                                        <Spinner className="animate-spin" size={24} />
                                    ) : (
                                        <Camera size={24} />
                                    )}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    onChange={handleAvatarUpload}
                                    className="hidden"
                                    data-testid="avatar-file-input"
                                />
                            </div>

                            <h3 className="font-semibold text-lg" data-testid="profile-display-name">
                                {profile.name || 'Kein Name'}
                            </h3>
                            <p className="text-sm text-muted-foreground">{profile.email}</p>

                            <Badge variant="outline" className="mt-2" data-testid="profile-role-badge">
                                {profile.role === 'admin' ? 'Administrator' : 'Makler'}
                            </Badge>

                            <div className="flex gap-2 mt-4">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadingAvatar}
                                    data-testid="change-avatar-btn"
                                >
                                    <Camera size={14} className="mr-1" />
                                    Bild ändern
                                </Button>
                                {profile.avatar && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="rounded-sm text-destructive hover:text-destructive"
                                        onClick={handleDeleteAvatar}
                                        data-testid="delete-avatar-btn"
                                    >
                                        <Trash size={14} />
                                    </Button>
                                )}
                            </div>

                            <Separator className="my-4" />

                            <div className="w-full text-left text-sm space-y-2 text-muted-foreground">
                                <p>Mitglied seit</p>
                                <p className="font-medium text-foreground">{formatDate(profile.created_at)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Profile Form */}
                <Card className="border lg:col-span-2" data-testid="profile-form-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                            <IdentificationCard size={22} />
                            Persönliche Daten
                        </CardTitle>
                        <CardDescription>
                            Diese Daten werden in Kontaktformularen und Exposés verwendet
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        {/* Name */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                    <User size={14} />
                                    Name
                                </Label>
                                <Input
                                    value={profile.name}
                                    onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
                                    placeholder="Vor- und Nachname"
                                    className="rounded-sm"
                                    data-testid="profile-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                    <EnvelopeSimple size={14} />
                                    E-Mail
                                </Label>
                                <Input
                                    value={profile.email}
                                    disabled
                                    className="rounded-sm bg-muted"
                                    data-testid="profile-email-input"
                                />
                                <p className="text-xs text-muted-foreground">E-Mail kann nicht geändert werden</p>
                            </div>
                        </div>

                        {/* Phone & Position */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                    <Phone size={14} />
                                    Telefon
                                </Label>
                                <Input
                                    value={profile.phone}
                                    onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                                    placeholder="+49 123 456 789"
                                    className="rounded-sm"
                                    data-testid="profile-phone-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                    <IdentificationCard size={14} />
                                    Position
                                </Label>
                                <Input
                                    value={profile.position}
                                    onChange={(e) => setProfile(p => ({ ...p, position: e.target.value }))}
                                    placeholder="z.B. Immobilienmakler"
                                    className="rounded-sm"
                                    data-testid="profile-position-input"
                                />
                            </div>
                        </div>

                        <Separator />

                        {/* Company */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-1.5">
                                <Buildings size={14} />
                                Firma
                            </Label>
                            <Input
                                value={profile.company}
                                onChange={(e) => setProfile(p => ({ ...p, company: e.target.value }))}
                                placeholder="Firmenname"
                                className="rounded-sm"
                                data-testid="profile-company-input"
                            />
                        </div>

                        {/* Address */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="sm:col-span-2 space-y-2">
                                <Label className="flex items-center gap-1.5">
                                    <MapPin size={14} />
                                    Adresse
                                </Label>
                                <Input
                                    value={profile.address}
                                    onChange={(e) => setProfile(p => ({ ...p, address: e.target.value }))}
                                    placeholder="Straße und Hausnummer"
                                    className="rounded-sm"
                                    data-testid="profile-address-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>PLZ</Label>
                                <Input
                                    value={profile.zip_code}
                                    onChange={(e) => setProfile(p => ({ ...p, zip_code: e.target.value }))}
                                    placeholder="10115"
                                    className="rounded-sm"
                                    data-testid="profile-zip-input"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Stadt</Label>
                                <Input
                                    value={profile.city}
                                    onChange={(e) => setProfile(p => ({ ...p, city: e.target.value }))}
                                    placeholder="Berlin"
                                    className="rounded-sm"
                                    data-testid="profile-city-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                    <Globe size={14} />
                                    Website
                                </Label>
                                <Input
                                    value={profile.website}
                                    onChange={(e) => setProfile(p => ({ ...p, website: e.target.value }))}
                                    placeholder="https://www.example.de"
                                    className="rounded-sm"
                                    data-testid="profile-website-input"
                                />
                            </div>
                        </div>

                        <Separator />

                        {/* Bio */}
                        <div className="space-y-2">
                            <Label>Über mich</Label>
                            <Textarea
                                value={profile.bio}
                                onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))}
                                placeholder="Kurze Beschreibung für Exposés und Kontaktseiten..."
                                className="rounded-sm min-h-[100px] resize-none"
                                data-testid="profile-bio-input"
                            />
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end">
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                className="rounded-sm"
                                data-testid="save-profile-btn"
                            >
                                {saving ? (
                                    <>
                                        <Spinner className="animate-spin mr-2" size={16} />
                                        Speichern...
                                    </>
                                ) : (
                                    <>
                                        <FloppyDisk size={16} className="mr-2" />
                                        Profil speichern
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Password Change */}
            <Card className="border" data-testid="password-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                        <Key size={22} />
                        Passwort ändern
                    </CardTitle>
                    <CardDescription>
                        Legen Sie ein neues Passwort für Ihr Konto fest
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label>Aktuelles Passwort</Label>
                            <Input
                                type="password"
                                value={passwords.current}
                                onChange={(e) => setPasswords(p => ({ ...p, current: e.target.value }))}
                                placeholder="Aktuelles Passwort"
                                className="rounded-sm"
                                data-testid="current-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Neues Passwort</Label>
                            <Input
                                type="password"
                                value={passwords.new}
                                onChange={(e) => setPasswords(p => ({ ...p, new: e.target.value }))}
                                placeholder="Mindestens 6 Zeichen"
                                className="rounded-sm"
                                data-testid="new-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Passwort bestätigen</Label>
                            <Input
                                type="password"
                                value={passwords.confirm}
                                onChange={(e) => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                                placeholder="Passwort wiederholen"
                                className="rounded-sm"
                                data-testid="confirm-password-input"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button
                            onClick={handleChangePassword}
                            disabled={savingPassword || !passwords.current || !passwords.new}
                            className="rounded-sm"
                            data-testid="change-password-btn"
                        >
                            {savingPassword ? (
                                <>
                                    <Spinner className="animate-spin mr-2" size={16} />
                                    Speichern...
                                </>
                            ) : (
                                <>
                                    <Key size={16} className="mr-2" />
                                    Passwort ändern
                                </>
                            )}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
